import { noop } from "es-toolkit/function";
import { type IDBPDatabase, openDB } from "idb";
import { getTimeWindowFieldLabelText } from "src/components/features/medium/AssetsFilter/components/TimeWindow/TimeWindow.utils";
import { deserializeFormParams } from "src/pages/ComparativeAnalysisPage/components/ComparativeAnalysisForm/ComparativeAnalysisForm.utils";
import { getRouteObject } from "@utils/appRoutes/appRoutes";
import {
  BASE_CONTEXT_LABELS,
  type COMPARE_RUNS_CONTEXT_LABELS,
} from "./AiToolboxSection.constants";
import type { SubagentDisplayInfo } from "./AiToolboxSection.types";

export function generateId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

// IndexedDB configuration.
// Threads/messages live server-side (ai-agents-manager); the only local store is
// the per-thread subagent transcript cache — LangGraph exposes subgraph state
// only while a run is in flight, so subagent transcripts must be snapshotted
// client-side to survive reloads. v1 held full chat history ("chat-history"),
// which is dropped on upgrade.
const DB_NAME = "definity-chat-db";
const DB_VERSION = 2;
// TODO(2026-12-28): drop the legacy store cleanup once browsers have had ~3 months
// to run the v2 upgrade.
const LEGACY_HISTORY_STORE = "chat-history";
const SUBAGENT_STORE = "subagent-display";

let dbInstance: Promise<IDBPDatabase> | null = null;

function getDB() {
  if (!dbInstance) {
    dbInstance = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (db.objectStoreNames.contains(LEGACY_HISTORY_STORE)) {
          db.deleteObjectStore(LEGACY_HISTORY_STORE);
        }
        if (!db.objectStoreNames.contains(SUBAGENT_STORE)) {
          db.createObjectStore(SUBAGENT_STORE);
        }
      },
    });
  }
  return dbInstance;
}

/** Per-thread map of `task` tool_call_id → subagent transcript snapshot */
export type SubagentDisplayEntries = Record<string, SubagentDisplayInfo>;

/**
 * IndexedDB cache of subagent transcripts, keyed by thread id.
 * Display fidelity only — never a source of truth for messages.
 */
export const subagentDisplayCache = {
  async get(threadId: string): Promise<SubagentDisplayEntries | undefined> {
    try {
      const db = await getDB();
      return await db.get(SUBAGENT_STORE, threadId);
    } catch {
      return undefined;
    }
  },
  async merge(threadId: string, entries: SubagentDisplayEntries): Promise<void> {
    try {
      const db = await getDB();
      const existing: SubagentDisplayEntries =
        (await db.get(SUBAGENT_STORE, threadId)) ?? {};
      await db.put(SUBAGENT_STORE, { ...existing, ...entries }, threadId);
    } catch {
      noop();
    }
  },
  async remove(threadId: string): Promise<void> {
    try {
      const db = await getDB();
      await db.delete(SUBAGENT_STORE, threadId);
    } catch {
      noop();
    }
  },
};

/** Context labels for AI chat context */
export const getContextLabels = (isClusterPage: boolean) =>
  ({
    ...BASE_CONTEXT_LABELS,
    task_name: isClusterPage ? "Cluster" : "Task Name",
  }) as const;

/**
 * Maps pathname to page type label for AI context.
 * Uses route matching from routes.ts for consistent path detection.
 */
export const getPageType = (pathname: string): string | null => {
  const route = getRouteObject(pathname);
  if (!route) return null;

  // Map route keys to page type labels with special handling for detail vs list views
  switch (route.key) {
    case "apps":
      return "Pipelines";
    case "alerts":
      return "Alerts";
    case "runs":
      return "Runs";
    case "datasets":
      // Check if it's a dataset detail page (has additional path segments)
      return /\/datasets\/[^/]+/.test(pathname) ? "Dataset" : "Datasets";
    case "compute":
      // Check if it's a cluster detail page (has additional path segments)
      return /\/computes?\/[^/]+/.test(pathname) ? "Cluster" : "Compute";
    case "compare-runs":
      return "Compare Runs";
    default:
      return null;
  }
};

/**
 * Formats a timeWindow value for AI context display.
 * Handles both numeric (minutes) and date range string formats.
 */
export function formatTimeWindowForContext(
  timeWindow: string | undefined
): string | undefined {
  if (timeWindow === undefined || timeWindow === null) return undefined;

  return getTimeWindowFieldLabelText(timeWindow);
}

/**
 * Resolves a compare-runs context value for a given key.
 * The URL uses compact paired params (e.g. `env=demo~~prod`) which are deserialized
 * into flat `{ env1, env2, pipeline1, ... }` before lookup.
 * Single source of truth for compare-runs value resolution — used by both
 * AiToolboxSection.hooks (AI prompt) and AiAssistantChatContext (chip display).
 */
export function getCompareRunsContextValue(
  key: keyof typeof COMPARE_RUNS_CONTEXT_LABELS,
  pageType: string | null,
  searchParams: URLSearchParams
): string | null {
  if (key === "page") return pageType;
  if (key === "tab") return searchParams.get("tab");
  const flat = deserializeFormParams(searchParams);
  const value = flat[key as keyof typeof flat];
  return value ?? null;
}

/**
 * Builds formatted context lines from labels and values.
 * Filters out empty values and formats as "Label = value" pairs.
 */
export function buildContextLines<T extends Record<string, string>>(
  labels: T,
  getValue: (key: keyof T) => string | number | null | undefined
): string | undefined {
  const lines = (Object.keys(labels) as (keyof T)[])
    .filter((key) => {
      const value = getValue(key);
      return value !== null && value !== undefined && value !== "";
    })
    .map((key) => {
      const value = getValue(key);
      return `${labels[key]} = "${value}"`;
    });
  return lines.length ? lines.join("\n") : undefined;
}
